import dayjs from 'dayjs';
import i18next from 'i18next';
import type {
  SubscriptionUseRequest,
  SubscriptionUseRequestStatus,
} from '../subscriptionUseRequest.types';

export class SubscriptionUseRequestModel {
  private readonly dto: SubscriptionUseRequest;

  private readonly _createdDate: string;
  private readonly _id: string;
  private readonly _planName: string;
  private readonly _productIconUrl: string;
  private readonly _productName: string;
  private readonly _seq: number;
  private readonly _status: SubscriptionUseRequestStatus;
  private readonly _updateDate: string;
  private readonly _userAccount: string;
  private readonly _userName: string;

  constructor(dto: SubscriptionUseRequest) {
    this.dto = dto;

    this._createdDate = dto?.createdDate ?? '';
    this._id = dto?.id ?? '';
    this._planName = dto?.planName ?? '';
    this._productIconUrl = dto?.productIconUrl ?? '';
    this._productName = dto?.productName ?? '';
    this._seq = dto?.seq ?? 0;
    this._status = dto?.status ?? 'CREATED';
    this._userAccount = dto?.userAccount ?? '';
    this._userName = dto?.userName ?? '';
    this._updateDate = dto?.updateDate ?? '';
  }

  get categoryId() {
    return this.dto.categoryId;
  }

  get updateDate(): string {
    return dayjs(this._updateDate).format('YYYY.MM.DD');
  }

  get createdDate(): DateTimeString {
    return dayjs(this._createdDate).format('YYYY.MM.DD');
  }

  get id(): string {
    return this._id;
  }

  get planName(): string {
    return this._planName;
  }

  get productIconUrl(): string {
    return this._productIconUrl;
  }

  get productName(): string {
    return this._productName;
  }

  get seq(): number {
    return this._seq;
  }

  get status(): SubscriptionUseRequestStatus {
    return this._status;
  }

  get statusStr(): '신청' | '진행중' | '대기' | '승인' | '반려' {
    switch (this.status) {
      case 'PENDING':
        return i18next.t('Subscrib_Detail_UseRequest_11') as '대기';
      case 'IN_PROGRESS':
        return i18next.t('Subscrib_Detail_UseRequest_34') as '진행중';
      case 'APPROVED':
        return i18next.t('Subscrib_Detail_UseRequest_12') as '승인';
      case 'REJECTED':
        return i18next.t('Subscrib_Detail_UseRequest_13') as '반려';
      default:
        return i18next.t('Subscrib_Detail_UseRequest_10') as '신청';
    }
  }

  get statusLabelColor(): 'yellow' | 'green' | 'gray' | 'pink' | 'gradient' {
    switch (this.status) {
      case 'CREATED':
        return 'gradient';
      case 'PENDING':
        return 'yellow';
      case 'APPROVED':
        return 'green';
      case 'REJECTED':
        return 'gray';
      case 'IN_PROGRESS':
      default:
        return 'pink';
    }
  }

  get statusBackgroundColor(): string {
    switch (this.status) {
      case 'CREATED':
        return 'bg-yellow';
      case 'IN_PROGRESS':
        return 'bg-pink';
      case 'APPROVED':
        return 'bg-green';
      case 'REJECTED':
        return 'bg-gray-dark';
      case 'PENDING':
        return 'bg-gray-dark';
      default:
        return 'bg-gray-dark';
    }
  }

  get statusTextColor(): string {
    switch (this.status) {
      case 'CREATED':
        return 'text-gray-sub-darker';
      case 'IN_PROGRESS':
        return 'text-white';
      case 'APPROVED':
        return 'text-white';
      case 'REJECTED':
        return 'text-gray-sub-darker';
      case 'PENDING':
        return 'text-gray-sub-darker';
      default:
        return 'text-white';
    }
  }

  get userAccount(): string {
    return this._userAccount;
  }

  get userName(): string {
    return this._userName;
  }
}
